import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, FileText, Users, HeartHandshake, ArrowRight, ShieldCheck, BookOpen, Quote } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import FounderSection from "@/components/FounderSection";
import CoachingSection from "@/components/CoachingSection";
import BookCover from "@/components/BookCover";
import { SITE } from "@/config/site";

const FEATURES = [
  {
    icon: Users,
    title: "Built for community leaders",
    body: "Designed for nonprofits, churches, schools, and grassroots founders. No grant-writing background required.",
  },
  {
    icon: FileText,
    title: "Seven proposal formats",
    body: "Full proposals, letters of inquiry, sponsorship asks, funder emails, and more — choose what you need.",
  },
  {
    icon: HeartHandshake,
    title: "Your voice, refined",
    body: "Answer simple questions in your own words. We organize them into clear, professional draft language.",
  },
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div data-testid={LANDING.hero}>
      {/* Nav */}
      <header className="nav-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-primary/10 grid place-items-center">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <span className="font-heading font-semibold text-lg text-slate-900">
              {SITE.brand}
            </span>
          </div>
          <Button
            variant="ghost"
            className="rounded-full hidden sm:inline-flex"
            onClick={() => navigate("/build")}
            data-testid="landing-nav-start-btn"
          >
            Start a Proposal
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="hero-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-in-up">
              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1.5 mb-6">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <span className="text-xs font-medium tracking-wide text-slate-700">
                  Grant-ready drafts in minutes
                </span>
              </div>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                Turn Your Funding Lane Into a
                <span className="text-primary"> Grant-Ready Proposal</span>
              </h1>

              <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
                Answer a few simple questions about who you serve, what problem you are
                solving, and what support you need. We will help organize your answers
                into a clear, polished proposal draft.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  className="rounded-full px-7 py-6 text-base bg-primary hover:bg-teal-900"
                  onClick={() => navigate("/build")}
                  data-testid={LANDING.startCta}
                >
                  Start My Proposal
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-7 py-6 text-base border-slate-200"
                  onClick={() => {
                    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  data-testid="landing-learn-btn"
                >
                  How it works
                </Button>
              </div>

              <div
                data-testid={LANDING.disclaimer}
                className="mt-8 flex items-start gap-2 text-xs text-slate-500 max-w-xl"
              >
                <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" />
                <span>
                  This tool helps draft proposal language. It does not guarantee funding.
                  Please review and personalize all information before submitting to a funder.
                </span>
              </div>

              {/* Testimonial card — small, subtle, supports the book/workshop */}
              <div
                className="mt-8 max-w-xl paper-surface p-5 sm:p-6 relative"
                data-testid="hero-testimonial-card"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Quote className="h-4 w-4 text-primary" />
                  <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-primary">
                    {SITE.testimonial.eyebrow}
                  </span>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed italic">
                  &ldquo;{SITE.testimonial.quote}&rdquo;
                </p>
                <p className="mt-3 text-xs font-semibold text-slate-600">
                  &mdash; {SITE.testimonial.attribution}
                </p>
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <p className="text-xs text-slate-600 leading-relaxed">
                    <span className="font-semibold text-primary">Impact:</span>{" "}
                    {SITE.testimonial.impact}
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative flex flex-col items-center lg:items-start fade-in-up">
                <span className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">
                  The Book
                </span>
                <h2
                  className="font-heading mt-2 text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight text-center lg:text-left"
                  data-testid="hero-book-header"
                >
                  {SITE.book.homepageFeature.header}
                </h2>
                <div className="gold-rule" />
                <p
                  className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed max-w-md text-center lg:text-left"
                  data-testid="hero-book-body"
                >
                  {SITE.book.homepageFeature.body}
                </p>

                <Link
                  to="/chasing-after-impact"
                  className="mt-6 group"
                  data-testid="hero-book-thumb"
                  aria-label="Chasing After Impact — Marie Thelusma-Chase, CFRE"
                >
                  <div className="transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-2xl">
                    <BookCover size="lg" />
                  </div>
                </Link>

                <Link
                  to="/chasing-after-impact"
                  className="inline-block mt-6"
                  data-testid="hero-book-cta"
                >
                  <Button
                    size="lg"
                    className="rounded-full px-7 py-6 text-base bg-primary hover:bg-teal-900"
                  >
                    <BookOpen className="h-4 w-4 mr-2" />
                    {SITE.book.homepageFeature.ctaLabel}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>

                <p className="mt-3 text-xs text-slate-500 italic max-w-[280px] text-center lg:text-left">
                  A companion book by {SITE.book.author}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder */}
      <FounderSection />

      {/* Features */}
      <section id="how-it-works" className="py-20 sm:py-24 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">
              How it works
            </span>
            <h2 className="font-heading mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
              From an idea to a fundable draft, the kind way.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              No jargon, no blank page. Just a guided conversation that captures what you
              already know about your community and turns it into proposal language.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                data-testid={LANDING.featureCard(i)}
                className="paper-surface p-7 hover:-translate-y-0.5 transition-transform"
              >
                <div className="h-11 w-11 rounded-xl bg-primary/10 grid place-items-center mb-5">
                  <f.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">
                  {f.title}
                </h3>
                <p className="mt-2 text-slate-600 leading-relaxed text-sm">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="paper-surface p-8 sm:p-12 text-center">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">
              Ready to turn your story into a proposal?
            </h2>
            <p className="mt-3 text-slate-600 max-w-xl mx-auto">
              Eight short steps. About ten minutes of your time. A draft you can edit and send.
            </p>
            <Button
              size="lg"
              className="mt-7 rounded-full px-8 py-6 text-base bg-primary hover:bg-teal-900"
              onClick={() => navigate("/build")}
              data-testid="landing-bottom-cta"
            >
              Start My Proposal <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Coaching */}
      <CoachingSection />

      <footer className="py-10 border-t border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between gap-4 text-sm text-slate-500">
          <div>© {new Date().getFullYear()} {SITE.brand}. For nonprofits and community builders.</div>
          <div>Helps draft proposal language. Does not guarantee funding.</div>
        </div>
      </footer>
    </div>
  );
}
